import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Habit } from "@/types/habit";
import { useAuth } from "@/components/auth/AuthProvider";
import { toast } from "@/hooks/use-toast";
import { Json } from "@/integrations/supabase/types";

export const useSupabaseHabits = () => {
  const [habits, setHabits] = useState<Habit[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    
    const fetchHabits = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("habits")
          .select("*")
          .order("created_at", { ascending: false });

        if (error) throw error;
        
        if (data) {
          // Map database fields to Habit type
          const mappedHabits: Habit[] = data.map(item => ({
            id: item.id,
            title: item.title,
            emoji: item.emoji || "",
            totalDays: item.total_days || 0,
            completedDays: item.completed_days || 0,
            reward: item.reward || "",
            rewardImage: item.reward_image,
            priority: (item.priority as Habit["priority"]) || "medium",
            isCompleted: item.is_completed || false,
            category: item.category || [],
            dailyTarget: item.daily_target ? {
              value: typeof item.daily_target === 'object' && item.daily_target !== null ? 
                     (item.daily_target as any).value || 0 : 0,
              unit: typeof item.daily_target === 'object' && item.daily_target !== null ? 
                    (item.daily_target as any).unit || "" : ""
            } : undefined,
            dailyProgress: item.daily_progress || 0
          }));
          
          // Sort habits by priority: high -> medium -> low
          const priorityOrder = { high: 0, medium: 1, low: 2 };
          const sortedHabits = mappedHabits.sort((a, b) => {
            return priorityOrder[a.priority] - priorityOrder[b.priority];
          });
          
          setHabits(sortedHabits);
        }

      } catch (error: any) {
        console.error("Error fetching habits:", error);
        toast({
          title: "Failed to load habits",
          description: error.message,
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchHabits();
    
    // Subscribe to real-time changes
    const habitChanges = supabase
      .channel('public:habits')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'habits' }, 
        () => {
          fetchHabits();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(habitChanges);
    };
  }, [user]);

  const handleAddHabit = async (newHabit: {
    title: string;
    emoji: string;
    totalDays: number;
    reward: string;
    priority: Habit["priority"];
  }) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from("habits")
        .insert({
          title: newHabit.title,
          emoji: newHabit.emoji,
          total_days: newHabit.totalDays,
          reward: newHabit.reward,
          priority: newHabit.priority,
          user_id: user.id
        });

      if (error) throw error;
      
      toast({
        title: "Habit added",
        description: "Your new habit has been created",
      });
    } catch (error: any) {
      console.error("Error adding habit:", error);
      toast({
        title: "Failed to add habit",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleComplete = async (id: string, dailyProgress?: number) => {
    if (!user) return;
    
    // Find the habit in local state first
    const localHabit = habits.find(h => h.id === id);
    if (!localHabit) return;
    
    const currentProgress = localHabit.dailyProgress || 0;
    const newProgress = dailyProgress !== undefined ? currentProgress + dailyProgress : currentProgress;
    
    const targetMet = localHabit.dailyTarget 
      ? newProgress >= localHabit.dailyTarget.value
      : true;

    const updatedCompletedDays = targetMet 
      ? Math.min(localHabit.completedDays + 1, localHabit.totalDays) 
      : localHabit.completedDays;
    const updatedIsCompleted = targetMet && (localHabit.completedDays + 1 >= localHabit.totalDays);
    const updatedDailyProgress = targetMet ? 0 : newProgress;

    // Optimistic update - update local state immediately
    setHabits(prevHabits => 
      prevHabits.map(habit =>
        habit.id === id
          ? {
              ...habit,
              dailyProgress: updatedDailyProgress,
              completedDays: updatedCompletedDays,
              isCompleted: updatedIsCompleted,
            }
          : habit
      )
    );
    
    try {
      const { error: updateError } = await supabase
        .from("habits")
        .update({
          daily_progress: updatedDailyProgress,
          completed_days: updatedCompletedDays,
          is_completed: updatedIsCompleted,
        })
        .eq("id", id);

      if (updateError) throw updateError;
      
      if (targetMet) {
        toast({
          title: "Daily goal completed!",
          description: `You've made progress on "${localHabit.title}"`,
        });
      } else {
        toast({
          title: "Progress tracked",
          description: `Progress updated for "${localHabit.title}"`,
        });
      }
    } catch (error: any) {
      console.error("Error completing habit:", error);
      // Revert optimistic update on error
      setHabits(prevHabits => 
        prevHabits.map(habit =>
          habit.id === id
            ? {
                ...habit,
                dailyProgress: currentProgress,
                completedDays: localHabit.completedDays,
                isCompleted: localHabit.isCompleted,
              }
            : habit
        )
      );
      toast({
        title: "Failed to update progress",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleRevert = async (id: string) => {
    if (!user) return;
    
    try {
      const { data: habit, error: fetchError } = await supabase
        .from("habits")
        .select("*")
        .eq("id", id)
        .single();

      if (fetchError) throw fetchError;
      
      const { error: updateError } = await supabase
        .from("habits")
        .update({
          completed_days: Math.max(0, habit.completed_days - 1),
          is_completed: false,
        })
        .eq("id", id);

      if (updateError) throw updateError;
      
      toast({
        title: "Progress reverted",
        description: `Removed one day from "${habit.title}"`,
      });
    } catch (error: any) {
      console.error("Error reverting habit:", error);
      toast({
        title: "Failed to revert progress",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from("habits")
        .delete()
        .eq("id", id);

      if (error) throw error;
      
      toast({
        title: "Habit deleted",
        description: "The habit has been removed",
      });
    } catch (error: any) {
      console.error("Error deleting habit:", error);
      toast({
        title: "Failed to delete habit",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleUpdateCategories = async (id: string, newCategories: string[]) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from("habits")
        .update({
          category: newCategories,
        })
        .eq("id", id);

      if (error) throw error;
      
      toast({
        title: "Categories updated",
        description: "Habit categories have been updated",
      });
    } catch (error: any) {
      console.error("Error updating categories:", error);
      toast({
        title: "Failed to update categories",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDuplicate = async (id: string) => {
    if (!user) return;
    
    try {
      // Get the habit to duplicate
      const { data: habitToDuplicate, error: fetchError } = await supabase
        .from("habits")
        .select("*")
        .eq("id", id)
        .single();

      if (fetchError) throw fetchError;
      
      // Create a new habit with the same properties
      const { error: insertError } = await supabase
        .from("habits")
        .insert({
          user_id: user.id,
          title: habitToDuplicate.title,
          emoji: habitToDuplicate.emoji,
          total_days: habitToDuplicate.total_days,
          completed_days: 0,
          reward: habitToDuplicate.reward,
          reward_image: habitToDuplicate.reward_image,
          priority: habitToDuplicate.priority,
          is_completed: false,
          category: habitToDuplicate.category,
          daily_target: habitToDuplicate.daily_target,
          daily_progress: 0,
        });

      if (insertError) throw insertError;
      
      toast({
        title: "Habit duplicated",
        description: "A copy of the habit has been created",
      });
    } catch (error: any) {
      console.error("Error duplicating habit:", error);
      toast({
        title: "Failed to duplicate habit",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleUpdateDailyTarget = async (id: string, value: number, unit: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from("habits")
        .update({
          daily_target: { value, unit },
          daily_progress: 0,
        })
        .eq("id", id);

      if (error) throw error;
      
      toast({
        title: "Daily target updated",
        description: "Habit daily target has been updated",
      });
    } catch (error: any) {
      console.error("Error updating daily target:", error);
      toast({
        title: "Failed to update daily target",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleUpdatePriority = async (id: string, priority: Habit["priority"]) => {
    if (!user) return;
    
    // Update local state immediately for instant feedback
    setHabits(prevHabits => {
      const updatedHabits = prevHabits.map(habit =>
        habit.id === id ? { ...habit, priority } : habit
      );
      
      // Re-sort habits by priority after update
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return updatedHabits.sort((a, b) => {
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      });
    });
    
    try {
      const { error } = await supabase
        .from("habits")
        .update({
          priority,
        })
        .eq("id", id);

      if (error) throw error;
      
      toast({
        title: "Priority updated",
        description: "Habit priority has been updated",
      });
    } catch (error: any) {
      console.error("Error updating priority:", error);
      // Revert local state on error
      setHabits(prevHabits => {
        const revertedHabits = prevHabits.map(habit =>
          habit.id === id ? { ...habit, priority: prevHabits.find(h => h.id === id)?.priority || "medium" } : habit
        );
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        return revertedHabits.sort((a, b) => {
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        });
      });
      
      toast({
        title: "Failed to update priority",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleUpdateTitle = async (id: string, title: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from("habits")
        .update({
          title,
        })
        .eq("id", id);

      if (error) throw error;
      
      toast({
        title: "Title updated",
        description: "Habit title has been updated",
      });
    } catch (error: any) {
      console.error("Error updating title:", error);
      toast({
        title: "Failed to update title",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return {
    habits,
    loading,
    handleAddHabit,
    handleComplete,
    handleRevert,
    handleDelete,
    handleUpdateCategories,
    handleDuplicate,
    handleUpdatePriority,
    handleUpdateTitle,
    handleUpdateDailyTarget,
  };
};
